import React, { useContext, createContext, useEffect, useState } from "react";
import { initializeApp, getApps } from "firebase/app";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";

// Check if running on localhost (emulator mode)
const isLocalhost = typeof window !== "undefined" &&
  window.location.hostname === "localhost";

// Development project ID - always use this for emulators regardless of which
// Firebase project is currently selected in the CLI. This ensures the React app
// and emulator are always using the same project context.
const DEV_PROJECT_ID = "dive-dev-f9d3f";

// When running locally, always use dev project config to match emulator context.
// This prevents CORS and permission errors when switching between firebase projects
// (e.g., `firebase use qa` or `firebase use production`) while developing locally.
const configDirect = isLocalhost
  ? {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: `${DEV_PROJECT_ID}.firebaseapp.com`,
      projectId: DEV_PROJECT_ID,
      storageBucket: `${DEV_PROJECT_ID}.appspot.com`,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    }
  : {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    };

const authContext = createContext();

// Global variable to hold the Firebase app instance
let firebaseAppInstance;
let isEmulatorConnected = false;
let db, auth, functions, storage;

// Idempotent [DEFAULT] app init. Exported for the magic-link landing page
// (AuthMagicPage), which must sign in on the SAME app instance the shell and
// route guards read — Firebase Auth persists sessions keyed by appName, so a
// session created on a named secondary app is invisible to the main app.
export const ensureFirebaseApp = () => {
  if (!firebaseAppInstance) {
    firebaseAppInstance =
      getApps().find((a) => a.name === "[DEFAULT]") ??
      initializeApp(configDirect);
  }
  return firebaseAppInstance;
};

//Wraps the app and makes auth object available to any child calling useAuth()
export const ProvideAuth = ({ children }) => {
  const authHook = useProvideAuth();
  return (
    <authContext.Provider value={authHook}>{children}</authContext.Provider>
  );
};

//Hook for child compoenets to get the auth object
//and re-render upon change.
export const useAuth = () => {
  return useContext(authContext);
};

//Provider hook that creates auth object and handles state
const useProvideAuth = () => {
  const [isLoadingFirebase, setIsLoadingFirebase] = useState(true);
  console.log("Triggering ProvideAuth Hook to run");

  useEffect(() => {
    if (!firebaseAppInstance) {
      console.log("Initializing Firebase App with project:", configDirect.projectId);
      if (isLocalhost) {
        console.log(
          `🏠 Local development mode: Using dev project (${DEV_PROJECT_ID}) for emulator compatibility`
        );
      }
    }
    ensureFirebaseApp();
    db = getFirestore(firebaseAppInstance);
    auth = getAuth(firebaseAppInstance);
    functions = getFunctions(firebaseAppInstance);
    storage = getStorage(firebaseAppInstance);

    if (isLocalhost && !isEmulatorConnected) {
      console.log("Connecting to Firebase emulators...");
      try {
        connectFirestoreEmulator(db, "localhost", 8080);
        connectAuthEmulator(auth, "http://localhost:9099");
        connectFunctionsEmulator(functions, "localhost", 5001);
        connectStorageEmulator(storage, "localhost", 9199);
        isEmulatorConnected = true;
        console.log("✅ All emulators connected successfully");
        console.log(
          "💡 TIP: Run emulators with: firebase emulators:start --project dive-dev-f9d3f"
        );
      } catch (error) {
        console.warn(
          "Emulator connection error (may already be connected):",
          error,
        );
        isEmulatorConnected = true; // Assume they're already connected
      }
    }
    setIsLoadingFirebase(false);
  }, []);

  return {
    firebaseAppInstance,
    db,
    auth,
    functions,
    storage,
    isLoadingFirebase,
  };
};
